import { Box, InputAdornment } from '@material-ui/core';
import classnames from 'classnames';
import { Loader, SGSChip, SGSInput, SGSSelect } from 'components';
import SGSFormAutoComplete from 'components/Form/AutoComplete';
import { useCustomStyles } from 'core/mui/customStyles';
import { delay } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './SGSColumnFilter.module.scss';

import { DatePicker } from '@material-ui/pickers';
import DatePickerToolbar from 'components/SGSDatePicker/DatePickerToolbar';
import moment from 'moment';

export type Filter = {
    [key: string]: string | object;
};
export type ColumnFilter =
    | string
    | {
          label: string;
          placeholder: string;
      };
export type ColumnsFilters = Record<string, ColumnFilter>;

type Props = {
    className?: string;
    filters: Filter[];
    onFiltersChange(value: Filter[]);
    //columns: ColumnsFilters; // TODO :: reativate columns
    columns: any;
    disabled?: boolean;
    customLabel?: string;
};

const SGSColumnFilter: React.FC<Props> = ({ className, filters, onFiltersChange, columns, disabled, customLabel }) => {
    const { t } = useTranslation();
    const [selectedColumn, setSelectedColumn] = useState('');
    const [searchValue, setSearchValue] = useState('');
    const [from, setFrom] = useState(null);
    const [until, setUntil] = useState(null);
    const [date, setDate] = useState(null);
    const availableOptions = useMemo(
        () => {
            return Object.keys(columns).map((key) => {
                const column = columns[key].value;
                // Reset period

                const fromFilter = filters.find((f) => f['period'] && f['period']['from']);
                if (!fromFilter) setFrom(null);

                const untilFilter = filters.find((f) => f['period'] && f['period']['until']);
                if (!untilFilter) setUntil(null);

                return {
                    label: typeof column === 'string' ? column : column.label,
                    value: key,
                };
            });
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [filters],
    );

    function onApplyFilter(e: React.KeyboardEvent<HTMLInputElement>) {
        if (e.key !== 'Enter') {
            return;
        }
        setSearchValue('');
        onFiltersChange([
            ...filters.filter((e) => !(Object.keys(e)[0] === selectedColumn)),
            {
                [selectedColumn]: (e.target as HTMLInputElement).value,
            },
        ]);
    }

    const handleFilterDropDownSelection = (value) => {
        setSearchValue('resset');
        onFiltersChange([
            ...filters.filter((e) => !(Object.keys(e)[0] === selectedColumn)),
            {
                [selectedColumn]: value.label,
            },
        ]);
        delay(setSearchValue, 10, '');
    };

    const handleFilterDropDownSelectionByValue = (value) => {
        setSearchValue('resset');
        onFiltersChange([
            ...filters.filter((e) => !(Object.keys(e)[0] === selectedColumn)),
            {
                [selectedColumn]: value.value,
            },
        ]);
        delay(setSearchValue, 10, '');
    };

    // Si la colonne sélectionnée n'est plus disponible, on repasse au premier élément
    useEffect(() => {
        if (selectedColumn && availableOptions.find((o) => o.value === selectedColumn)) {
            return;
        }
        setSelectedColumn(availableOptions[0]?.value);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [availableOptions]);

    // Get filter type (input, autocomplete, etc.)
    const filterType = columns[selectedColumn]?.type;

    // Get options if filter type is autocomplete
    const options = columns[selectedColumn]?.options;

    // Custom MUI styles filters
    const classes = useCustomStyles();

    const handleDateFrom = (value) => {
        setFrom(value);
        onFiltersChange([
            ...filters.filter((e) => !(Object.keys(e)[0] === selectedColumn)),
            {
                [selectedColumn]: { from: moment(value).format('YYYY/MM/DD'), until },
            },
        ]);
        delay(setSearchValue, 10, '');
    };

    const handleDateUntil = (value) => {
        setUntil(value);
        onFiltersChange([
            ...filters.filter((e) => !(Object.keys(e)[0] === selectedColumn)),
            {
                [selectedColumn]: { from, until: moment(value).format('YYYY/MM/DD') },
            },
        ]);
        delay(setSearchValue, 10, '');
    };

    const handleDate = (value) => {
        setDate(value);
        onFiltersChange([
            ...filters.filter((e) => !(Object.keys(e)[0] === selectedColumn)),
            {
                [selectedColumn]: moment(value).format('YYYY-MM-DD'),
            },
        ]);
        delay(setSearchValue, 10, '');
    };

    const getLabel = (value, column) => {
        if (column?.type === 'period') {
            if (value?.from && value?.until == null) return `A partir du ${moment(value.from).format('DD/MM/YYYY')}`;
            if (value?.from && value?.until !== null) return `Du ${moment(value.from).format('DD/MM/YYYY')} au ${moment(value.until).format('DD/MM/YYYY')}`;
            if (value?.from === null && value?.until !== null) return `Jusqu'au ${moment(value.until).format('DD/MM/YYYY')}`;
        }
        if (column?.type === 'date') {
            return moment(value, 'YYYY/MM/DD').format('DD/MM/YYYY');
        }

        return value;
    };

    return (
        <Box className={className}>
            <Box className={styles.columnFilter}>
                <Box className={styles.columnFilter__titleContainer}>
                    <SGSSelect
                        fullWidth
                        value={selectedColumn}
                        className={styles.columnFilter__select}
                        onChange={(e) => {
                            setSelectedColumn((e.target.value as string) || undefined);
                        }}
                        disableUnderline
                        label={customLabel ? customLabel : t('filters.on')}
                        options={availableOptions}
                        classes={{
                            select: classes.select,
                        }}
                    />
                </Box>
                <Box className={styles.columnFilter__inputContainer} style={{ width: '500px' }}>
                    {filterType === 'input' && (
                        <SGSInput
                            fullWidth
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value || '')}
                            className={styles.columnFilter__input}
                            InputProps={{
                                disableUnderline: true,
                                endAdornment: disabled && (
                                    <InputAdornment position="end">
                                        <Loader loading={disabled} small />
                                    </InputAdornment>
                                ),
                            }}
                            label={disabled ? t('global.pleaseWait') : (selectedColumn && (columns[selectedColumn] as any).placeholder) ?? t('filters.byColumn')}
                            onKeyDown={onApplyFilter}
                            disabled={disabled}
                            classes={{ root: classes.root }}
                        />
                    )}
                    {filterType === 'autocomplete' && (
                        <SGSFormAutoComplete
                            fullWidth={true}
                            variant="standard"
                            name="columnFilter"
                            label=""
                            options={options}
                            loading={false} // TODO :: add loading
                            handleChange={handleFilterDropDownSelection}
                            value={searchValue || null}
                        />
                    )}
                    {filterType === 'autocompleteByValue' && (
                        <SGSFormAutoComplete
                            fullWidth={true}
                            variant="standard"
                            name="columnFilter"
                            label=""
                            options={options}
                            loading={false} // TODO :: add loading
                            handleChange={handleFilterDropDownSelectionByValue}
                            value={searchValue || null}
                        />
                    )}
                    {filterType === 'period' && (
                        <Box display="flex">
                            <Box className="pr-5" style={{ marginTop: '-16px', padding: 0, height: '32px;' }}>
                                <DatePicker
                                    label={t('compensation.search.from')}
                                    value={from}
                                    onChange={handleDateFrom}
                                    name="from"
                                    format="dd/MM/yyyy"
                                    ToolbarComponent={DatePickerToolbar}
                                    views={['year', 'month', 'date']}
                                    autoOk={true}
                                    cancelLabel={t('global.cancel')}
                                    clearLabel={t('global.clear')}
                                    okLabel={t('global.ok')}
                                    todayLabel={t('global.today')}
                                />
                            </Box>
                            <Box style={{ marginTop: '-16px', padding: 0, height: '32px;' }}>
                                <DatePicker
                                    label={t('compensation.search.until')}
                                    value={until}
                                    onChange={handleDateUntil}
                                    name="until"
                                    format="dd/MM/yyyy"
                                    ToolbarComponent={DatePickerToolbar}
                                    views={['year', 'month', 'date']}
                                    autoOk={true}
                                    cancelLabel={t('global.cancel')}
                                    clearLabel={t('global.clear')}
                                    okLabel={t('global.ok')}
                                    todayLabel={t('global.today')}
                                />
                            </Box>
                        </Box>
                    )}
                    {filterType === 'date' && (
                        <Box display="flex">
                            <Box className="pr-5" style={{ marginTop: '-16px', padding: 0, height: '32px;' }}>
                                <DatePicker label={customLabel ? customLabel : t('compensation.search.dateSinitre')} value={date} onChange={handleDate} name="date" format="dd/MM/yyyyy" />
                            </Box>
                        </Box>
                    )}
                </Box>
            </Box>
            <Box className="mt-2">
                <Box component="span">{t('cotisations.call.activeFilters')} : </Box>
                {filters.map((f) =>
                    Object.keys(f).map((k) =>
                        columns[k]?.value ? (
                            <SGSChip className="ml-2" label={`${columns[k].value} : ${getLabel(f[k], columns[k])}`} onDelete={() => onFiltersChange(filters.filter((o) => o !== f))} />
                        ) : null,
                    ),
                )}

                {filters.length > 0 && (
                    <Box component="span" className={classnames(styles.columnFilter__delete, 'ml-2')} onClick={() => onFiltersChange([])}>
                        <i className="fas fa-trash-alt mr-2" />
                        <span>{t('filters.delete')}</span>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default SGSColumnFilter;
